import * as React from "react";
import { IFtthOffer } from "@nju/data-access-portal-offers";
import { pushBeginCheckoutEventGA4 } from "@nju/data-layer";
import {
  A,
  Badge,
  BlockText,
  Button,
  Drawer,
  IconListElement,
  Panel,
  Separator,
  Text,
  useOverlay,
} from "@nju/ui";
import { formatPrice } from "@nju/utils";
import { FibercoOfferDetails } from "../FibercoOfferDetails";

interface ISelectedOffer {
  isSfh: boolean;
  isLoading: boolean;
  offer: IFtthOffer;
  setOffer: (offerId: string) => void;
  selectedOffer: string | undefined;
  withBadge: boolean;
  filteredOffers: IFtthOffer[];
}

export function SelectedOffer({
  isSfh,
  isLoading,
  offer,
  setOffer,
  selectedOffer,
  withBadge,
  filteredOffers,
}: ISelectedOffer) {
  const drawer = useOverlay();

  const handleSetOffer = React.useCallback(() => {
    pushBeginCheckoutEventGA4({
      productId: offer.offerId,
      productPrice: offer.totalRecurringPrice,
      downloadSpeed: String(offer.downloadSpeed),
      offerCategory: "Internet stacjonarny",
      subscriptionCategory: "Nowy",
      numberCount: 1,
    });

    setOffer(offer.offerId);
  }, [offer, setOffer]);

  return (
    <>
      {drawer.isOpened && (
        <Drawer onClose={drawer.close}>
          <FibercoOfferDetails
            flow="onboarding"
            isLoading={isLoading}
            offer={offer}
            selectedOffer={selectedOffer}
            setOffer={setOffer}
          />
        </Drawer>
      )}
      <div className="max-w-[420px]">
        <Panel display="flex" testId="offer-panel">
          <div>
            <div className="flex gap-2">
              {offer?.labels?.map((offer) => {
                return (
                  <>
                    {offer === "Nowość" ? (
                      <>
                        {withBadge && <Badge badgeText={offer} fontSize={14} />}
                      </>
                    ) : (
                      <Badge
                        badgeText={offer}
                        fontSize={14}
                        name="percentage"
                        variant="dark"
                      />
                    )}
                  </>
                );
              })}
            </div>
            {(!offer?.labels || offer?.labels?.length === 0) && (
              <div className="h-6" />
            )}
            <Separator size="xxs" tone="invisible" />
            <BlockText>
              <div
                className={`${filteredOffers.length !== 4 ? "inline" : ""}`}
                data-cy="selected-speed"
              >
                <Text>{`nju światłowód do ${offer.downloadFullText}`}</Text>
              </div>
              <A
                display="inline"
                onClick={drawer.open}
                size="inline"
                variant="simple"
              >
                <div className="inline" data-cy="details-button">
                  <Text
                    className={`${filteredOffers.length !== 4 ? "ml-3" : ""}`}
                    size="m"
                    tone="accent"
                    weight="bold"
                    withUnderline={true}
                  >
                    szczegóły
                  </Text>
                </div>
              </A>
            </BlockText>
            <Separator size="xxs" tone="invisible" />
            <div className="inline" data-cy="price-of-selected-offer">
              <Text size="keyM" weight="bold">
                {formatPrice(offer.totalRecurringPrice, false)}
              </Text>{" "}
            </div>
            zł miesięcznie
            {isSfh && (
              <>
                <Separator size="xxs" tone="invisible" />
                <BlockText tone="neutral">
                  <Text weight="bold">
                    +{" "}
                    {offer.ftthSfhPrice &&
                      formatPrice(offer.ftthSfhPrice, false)}{" "}
                    zł miesięcznie
                  </Text>
                  <Separator size="none" tone="invisible" />
                  opłata za internet w budynku jednorodzinnym
                </BlockText>
              </>
            )}
            <Separator size="m" />
            <IconListElement
              iconAlign="start"
              iconName="history"
              iconSize="small"
              mode="raw"
              text={`umowa na czas nieokreślony - rezygnujesz, kiedy chcesz`}
            />
            <IconListElement
              iconAlign="start"
              iconName="tachometer-alt-fastest"
              iconSize="small"
              mode="raw"
              text={
                <BlockText className="max-w-[130px] from-tablet:max-w-[150px]">
                  <Text size={{ "@onlyMobile": "s" }}>
                    prędkość pobierania do {offer.downloadFullText}
                  </Text>
                  <Separator size="none" tone="invisible" />
                  <Text size={{ "@onlyMobile": "s" }}>
                    prędkość wysyłania do {offer.uploadFullText}
                  </Text>
                </BlockText>
              }
            />
            <IconListElement
              iconAlign="start"
              iconName="user-hard-hat"
              iconSize="small"
              mode="raw"
              text={`opłata instalacyjna ${formatPrice(
                offer.totalOneTimePrice,
              )} zł jednorazowo`}
            />
            <Separator size="s" tone="invisible" />
            <Button
              data-cy={offer.offerId}
              display="block"
              isLoading={isLoading && selectedOffer === offer.offerId}
              onPress={handleSetOffer}
              size={{ "@onlyMobile": "s" }}
              variant="primary"
            >
              wybierz
            </Button>
          </div>
        </Panel>
      </div>
    </>
  );
}
